import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  ClientReportRecipientsResponse,
  ClientReportSchedule,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { Authorizations } from "~/api/types";
import { useTeam } from "~/contexts/TeamProvider";
import { tryCatch } from "~/helpers/tryCatch";

import { hasSiteAccess, listLabel, memberName } from "./delivery";

// The API has no page size cap, and a team this big is unheard of.
const MEMBERS_PAGE_SIZE = 500;

/**
 * The site's report schedule, the members and the recipient list it emails,
 * and who may change them.
 */
export const useReportDelivery = (siteId: number) => {
  const { team } = useTeam();

  const scheduleQuery = useQuery({
    queryKey: queryKeys.clientReportSchedule(siteId),
    queryFn: async (): Promise<ClientReportSchedule | null> => {
      const { data, error } = await tryCatch(
        apiClient.sites.clientReportScheduleDetail(siteId),
      );

      if (error) {
        // A site without a schedule answers 404.
        if (error.status === 404) return null;
        throw error;
      }

      return data.data.client_report_schedule;
    },
  });

  const recipientsQuery = useQuery({
    queryKey: queryKeys.clientReportRecipients(siteId),
    queryFn: async () => {
      const response = await apiClient.sites.clientReportRecipientsDetail(
        siteId,
        { headers: { "Authorize-Collection-Actions": "create" } },
      );
      const data = response.data as ClientReportRecipientsResponse & {
        authorizations?: Authorizations;
      };

      return {
        recipients: data.client_report_recipients,
        authorizations: data.authorizations,
      };
    },
  });

  const membersQuery = useQuery({
    queryKey: queryKeys.teamMembershipsAll(team?.id),
    queryFn: async () => {
      const response = await apiClient.memberships.membershipsList({
        "page[size]": MEMBERS_PAGE_SIZE,
        s: "user_first_name ASC, user_last_name ASC, user_email ASC",
      });

      return response.data.memberships;
    },
    enabled: Boolean(team?.id),
  });

  // The backend lets the same people (team admins, outside standby)
  // choose recipients, set the schedule and email a report.
  const canManage = Boolean(
    recipientsQuery.data?.authorizations?.collection?.includes("create"),
  );

  // Team admins only; anyone else is refused.
  const listsQuery = useQuery({
    queryKey: queryKeys.reportRecipientLists(team?.id),
    queryFn: async () => {
      const response =
        await apiClient.reportRecipientLists.reportRecipientListsList();

      return response.data.report_recipient_lists;
    },
    enabled: canManage && Boolean(team?.id),
  });

  const members = useMemo(
    () =>
      (membersQuery.data ?? []).filter((membership) =>
        hasSiteAccess(membership, siteId),
      ),
    [membersQuery.data, siteId],
  );

  // Only members who can see the site are emailed; rows of members who lost
  // access are left alone.
  const recipients = useMemo(
    () =>
      (recipientsQuery.data?.recipients ?? []).filter((recipient) =>
        members.some(({ id }) => id === recipient.membership_id),
      ),
    [recipientsQuery.data, members],
  );
  const recipientNames = useMemo(
    () =>
      recipients.map((recipient) =>
        memberName(members.find(({ id }) => id === recipient.membership_id)!),
      ),
    [recipients, members],
  );

  const lists = useMemo(() => listsQuery.data ?? [], [listsQuery.data]);
  const listId = scheduleQuery.data?.report_recipient_list_id ?? null;

  return {
    schedule: scheduleQuery.data,
    recipients,
    recipientNames,
    members,
    /** The team's recipient lists; empty for anyone but admins. */
    lists,
    /** The list the site's reports are emailed to, kept on its schedule. */
    listId,
    listLabel: listLabel(
      listId,
      lists.find(({ id }) => id === listId),
    ),
    canManage,
    isLoading:
      scheduleQuery.isLoading ||
      recipientsQuery.isLoading ||
      membersQuery.isLoading ||
      listsQuery.isLoading,
    error:
      scheduleQuery.error ??
      recipientsQuery.error ??
      membersQuery.error ??
      listsQuery.error,
  };
};

export type ReportDelivery = ReturnType<typeof useReportDelivery>;
